import {
  LinkTag, MetaTag, ScriptTag, TitleTag
} from '@thd-nucleus/thd-helmet';

// Maps tag keys to their corresponding tag generator functions.
const tagGenerators = {
  title: (value) => new TitleTag(value),
  description: (value) => new MetaTag({ name: 'description', content: value, id: 'description' }),
  fbAdmins: (value) => new MetaTag({ property: 'fb:admins', content: value, id: 'fbAdmins' }),
  ogDescription: (value) => new MetaTag({ property: 'og:description', content: value, id: 'ogDescription' }),
  ogImage: (value) => new MetaTag({ property: 'og:image', content: value, id: 'ogImage' }),
  ogSiteName: (value) => new MetaTag({ property: 'og:site_name', content: value, id: 'ogSiteName' }),
  ogTitle: (value) => new MetaTag({ property: 'og:title', content: value, id: 'ogTitle' }),
  ogType: (value) => new MetaTag({ property: 'og:type', content: value, id: 'ogType' }),
  ogUrl: (value) => new MetaTag({ property: 'og:url', content: value, id: 'ogUrl' }),
  canonical: (value) => new LinkTag({ rel: 'canonical', href: value, id: 'canonical' }),
  alternate: (value) => {
    if (Array.isArray(value)) {
      return value
        .map(({ href, hreflang, id }) => {
          if (!href) return null;
          const attrs = { rel: 'alternate', href };
          if (hreflang) attrs.hreflang = hreflang;
          if (id) attrs.id = id;
          return new LinkTag(attrs);
        })
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return new LinkTag({ rel: 'alternate', href: value });
    }

    return null;
  },
  googleSiteVerification: (value) => new MetaTag({
    name: 'google-site-verification',
    content: value,
    id: 'gscVerification'
  }),
  robots: (isNoFollow) => new MetaTag({
    name: isNoFollow ? 'ROBOTS' : 'robots',
    content: isNoFollow ? 'NOINDEX, NOFOLLOW' : 'max-image-preview:large',
    id: 'robots'
  }),
  // Generates an array of preload LinkTags for each image object in the array
  preloadImages: (images) => {
    if (!Array.isArray(images)) {
      return [];
    }
    return images.map((img, idx) => {
      if (!img || !img.href) return null;
      const {
        href,
        rel = 'preload',
        id = `preloadImg${idx}`,
        as = 'image',
        media = '(max-width: 600px)',
        fetchpriority = 'high'
      } = img;
      return new LinkTag({
        rel, href, id, as, media, fetchpriority
      });
    }).filter(Boolean);
  },
  // Generates an array of ScriptTag objects for each schema object in the array
  schemas: (schemas) => {
    if (!Array.isArray(schemas)) {
      return [];
    }
    return schemas.map((schema) => {
      if (!schema || !schema.content || !schema.id) {
        return null;
      }
      return new ScriptTag({
        content: schema.content,
        id: schema.id
      });
    }).filter(Boolean);
  }
};

// Returns a tag object for a given tag name and value, or null if not supported or value is falsy.
const generateTag = (tagName, tagValue) => {
  // Disallow undefined, null, empty string, 0, NaN, etc., but keep boolean false valid
  if (!tagValue && tagValue !== false) {
    return null;
  }

  const generator = tagGenerators[tagName];

  if (!generator) {
    return null;
  }

  return generator(tagValue);
};

/**
 * Generates an array of tag objects (TitleTag, MetaTag, LinkTag) for use in document head metadata.
 * Each key in the input object corresponds to a supported tag type and is mapped to its appropriate tag.
 *
 * Supported tag keys:
 * - title
 * - description
 * - fbAdmins
 * - googleSiteVerification
 * - ogDescription
 * - ogImage
 * - ogSiteName
 * - ogTitle
 * - ogType
 * - ogUrl
 * - canonical
 * - alternate
 * - preloadImages
 * - schemas
 * - robots
 *
 * @param {Object} args - An object where keys are tag types (see above) and values are their contents.
 * @returns {Array} An array of tag objects for use with @thd-nucleus/thd-helmet.
 */
export const generateTags = (args) => Object.entries(args)
  .flatMap(([key, value]) => {
    const tag = generateTag(key, value);
    // Flatten arrays (like from preloadImages), otherwise return as array
    return Array.isArray(tag) ? tag : [tag];
  })
  .filter(Boolean);
